import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { validateOAuthCallback } from '../utils/accountLinking';

export const AuthCallbackPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const [hasExecuted, setHasExecuted] = useState(false);
  const navigate = useNavigate();

  console.log('🔄 AuthCallbackPage render, hasExecuted:', hasExecuted);

  useEffect(() => {
    console.log('🔄 AuthCallbackPage useEffect running, hasExecuted:', hasExecuted);
    
    if (hasExecuted) {
      console.log('⚠️ Already executed, skipping');
      return;
    }
    
    // Execute immediately, no delay
    console.log('🔄 About to execute handleAuthCallback');
    setHasExecuted(true);
    handleAuthCallback();
  }, [navigate, hasExecuted]);

  const handleAuthCallback = async () => {
    try {
      console.log('🔄 Processing OAuth callback...');
      
      // Modern approach: Let Supabase handle the OAuth callback automatically
      // Just wait for the session to be established
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error(`Failed to get session: ${sessionError.message}`);
      }
      
      if (!session) {
        console.log('⏳ No session found - this could be duplicate account prevention...');
        
        // Wait a bit for OAuth to process, then check again
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
        
        if (retryError) {
          throw new Error(`Session retry failed: ${retryError.message}`);
        }
        
        if (!retrySession) {
          console.log('🛡️ No session after retry - likely duplicate account prevention worked');
          // This is expected when duplicate account is prevented
          // Redirect back to login with appropriate error message
          setStatus('error');
          setMessage('This email is already registered with a different sign-in method. Please use Email/Password to sign in.');
          
          setTimeout(() => {
            navigate('/login?error=duplicate_account');
          }, 3000);
          return;
        }
        
        console.log('✅ Session established after retry:', retrySession.user.email);
      } else {
        console.log('✅ Session found immediately:', session.user.email);
      }

      const finalSession = session || await supabase.auth.getSession().then(r => r.data.session);
      
      if (!finalSession) {
        throw new Error('Could not establish authenticated session');
      }

      // Validate the OAuth callback to prevent duplicate accounts
      console.log('🔍 Validating OAuth callback for email:', finalSession.user.email);
      
      let validation;
      try {
        validation = await validateOAuthCallback(finalSession);
        console.log('🔍 Validation result:', validation);
      } catch (validationError) {
        console.error('❌ Validation threw error:', validationError);
        // If validation fails due to network/server issues, allow login but log the error
        validation = { success: true, message: 'Validation skipped due to server error' };
      }

      if (!validation.success) {
        console.log('❌ Validation failed, showing duplicate account error');
        setStatus('error');
        setMessage(validation.message);
        
        // Redirect to login page after showing error
        setTimeout(() => {
          navigate('/login?error=duplicate_account');
        }, 3000);
        return;
      }

      // Success - redirect to appropriate page
      setStatus('success');
      setMessage('Authentication successful! Redirecting...');
      
      setTimeout(() => {
        // Check if user was trying to purchase something
        const pendingPurchase = sessionStorage.getItem('pendingPurchase');
        if (pendingPurchase) {
          // Don't clear it yet - let pricing page handle the purchase
          navigate('/pricing');
        } else {
          navigate('/');
        }
      }, 1500);

    } catch (error: any) {
      console.error('Auth callback error:', error);
      
      setStatus('error');
      setMessage('Authentication failed. Please try signing in again.');
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/login?error=auth_failed');
      }, 3000);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center'
      }}>
        
        {/* Loading State */}
        {status === 'loading' && (
          <>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f4f6',
              borderTop: '3px solid #2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1f2937',
              margin: '0 0 8px 0'
            }}>
              Authenticating...
            </h2>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              margin: 0
            }}>
              {message}
            </p>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px'
            }}>
              ✓
            </div>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#10b981',
              margin: '0 0 8px 0'
            }}>
              Success!
            </h2>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              margin: 0
            }}>
              {message}
            </p>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px',
              color: 'white'
            }}>
              ✕
            </div>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#ef4444',
              margin: '0 0 8px 0'
            }}>
              Authentication Failed
            </h2>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              margin: 0,
              lineHeight: '1.4'
            }}>
              {message}
            </p>
            <p style={{ 
              color: '#9ca3af', 
              fontSize: '12px',
              margin: '12px 0 0 0'
            }}>
              Redirecting to login page...
            </p>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}; 